import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Loader2, X } from 'lucide-react';
import type { CursoAcademy } from '../academyApi';
import { datosDelCertificado, nombreDeArchivoCertificado } from './datosDelCertificado';
import { generarCertificado, type DatosCertificado } from './generarCertificado';
import { VistaCertificado } from './VistaCertificado';
import { descargarDesdeBlob } from '../../../utils/descargarArchivo';

interface Props {
  curso: CursoAcademy;
  onCerrar: () => void;
}

/**
 * El certificado del curso: **primero se ve, después se baja**
 * (ADR-2026-08-25-el-certificado-se-mira-antes-de-bajarlo).
 *
 * Antes el único acceso era un botón que descargaba un PDF a ciegas. Un certificado es
 * algo que la persona quiere MIRAR —tiene su nombre— y bajar un archivo sin saber qué
 * trae obliga a abrirlo en otra app para enterarse. Acá se ve la lámina completa y el
 * botón de descarga queda al lado, para quien la quiera guardar o imprimir.
 *
 * Lo que se muestra es la misma lámina que sale en el PDF (ver `VistaCertificado`), con
 * los mismos datos: no es una "vista previa aproximada".
 */
export function ModalCertificado({ curso, onCerrar }: Props) {
  const [datos, setDatos] = useState<DatosCertificado | null>(null);
  const [estado, setEstado] = useState<'cargando' | 'listo' | 'error'>('cargando');
  const [bajando, setBajando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState(false);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const d = await datosDelCertificado(curso);
        if (!vivo) return;
        setDatos(d);
        setEstado(d ? 'listo' : 'error');
      } catch (e) {
        console.error('[academia] no se pudieron cargar los datos del certificado', e);
        if (vivo) setEstado('error');
      }
    })();
    return () => {
      vivo = false;
    };
  }, [curso]);

  /* El `onCerrar` que llega es una función nueva en cada render del padre, así que
     un efecto que dependa de ella se rehace todo el tiempo. Con el bloqueo de scroll
     adentro eso era un bug real: la segunda vuelta capturaba `'hidden'` como valor
     "previo" y al cerrar el modal el `body` quedaba sin scroll
     (ADR-2026-08-25-el-bloqueo-de-scroll-del-modal-se-monta-una-sola-vez).

     Se separa en dos: el bloqueo de scroll se monta y desmonta UNA vez, y el Escape
     lee el callback desde una ref para no tener que depender de su identidad. */
  const cerrarRef = useRef(onCerrar);
  cerrarRef.current = onCerrar;

  useEffect(() => {
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = overflowPrevio;
    };
  }, []);

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cerrarRef.current();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, []);

  const descargar = async () => {
    if (!datos || bajando) return;
    setBajando(true);
    setErrorDescarga(false);
    try {
      const blob = await generarCertificado(datos);
      await descargarDesdeBlob({
        blob,
        fileName: nombreDeArchivoCertificado(curso),
        mime: 'application/pdf',
      });
    } catch (e) {
      console.error('[academia] no se pudo generar el certificado', e);
      setErrorDescarga(true);
    } finally {
      setBajando(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-brand-black/60 p-3 backdrop-blur-sm sm:p-6"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-label="Tu certificado"
    >
      {/* El click de adentro no cierra: sin esto, arrastrar para seleccionar el nombre
          del certificado cierra el modal a mitad de camino. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-full w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl sm:p-6"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-[17px] font-bold tracking-[-0.01em] text-brand-black/90">
              Tu certificado
            </h2>
            <p className="mt-0.5 truncate text-[13px] text-black/45">{curso.titulo}</p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-black/40 transition-colors hover:bg-black/[0.05] hover:text-black/70"
          >
            <X className="h-4.5 w-4.5" />
          </button>
        </div>

        {estado === 'cargando' && (
          /* El hueco tiene la proporción exacta de la lámina: así el modal no cambia
             de tamaño cuando el certificado aparece. */
          <div className="grid w-full animate-pulse place-items-center rounded-xl bg-black/[0.04] [aspect-ratio:1056/816]">
            <Loader2 className="h-5 w-5 animate-spin text-black/25" />
          </div>
        )}

        {estado === 'error' && (
          <div className="grid w-full place-items-center rounded-xl bg-amber-50 p-6 text-center [aspect-ratio:1056/816]">
            <p className="max-w-sm text-[13.5px] leading-relaxed text-amber-800">
              No pudimos armar tu certificado. Puede que se haya cerrado la sesión —
              probá recargar la página.
            </p>
          </div>
        )}

        {estado === 'listo' && datos && (
          <div className="overflow-hidden rounded-xl ring-1 ring-black/[0.08]">
            <VistaCertificado nombre={datos.nombre} curso={datos.curso} fecha={datos.fecha} />
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-end gap-2.5">
          {errorDescarga && (
            <p className="mr-auto text-[12.5px] text-amber-700">
              No se pudo generar el PDF. Probá de nuevo.
            </p>
          )}
          <button
            type="button"
            onClick={onCerrar}
            className="rounded-xl px-4 py-2.5 text-[13.5px] font-medium text-black/50 transition-colors hover:text-brand-primary"
          >
            Cerrar
          </button>
          <button
            type="button"
            onClick={descargar}
            disabled={estado !== 'listo' || bajando}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-primary px-4 py-2.5 text-[13.5px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:bg-black/[0.08] disabled:text-black/35"
          >
            {bajando ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Generando…
              </>
            ) : (
              <>
                <Download className="h-3.5 w-3.5" strokeWidth={2.2} />
                Descargar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
